import type { BaseDocument } from '../mongo/base-document.interface';
import type { ModuleAreaKey } from '../modules';
import { MODULE_AREA } from '../modules';
import type { BuiltInRoleKey } from './built-in';

export const ROLE_SCOPE = {
  carrier: 'carrier',
  location: 'location',
  tenant: 'tenant',
  territory: 'territory',
} as const;

export type RoleScope = (typeof ROLE_SCOPE)[keyof typeof ROLE_SCOPE];

export interface UserScope {
  id: string;
  level: RoleScope;
}

export type RoleFunctions = Record<string, never>;

/**
 * The scope level(s) a role needs a user to hold. `null` = none; a single level = that level; an array
 * (SWOOP-378) = ANY ONE of those levels is enough, for a role that may sit at more than one level.
 * Read it through {@link getRequiredScopeLevels} rather than branching on the shape.
 */
export type RoleRequiredScope = RoleScope | readonly RoleScope[] | null;

export const ROLE_REQUIRED_SCOPE: Record<BuiltInRoleKey, RoleRequiredScope> = {
  ACCOUNT_MANAGER: null,
  // SWOOP-200: no scope is required for DRIVER. Territory association for drivers is owned
  // by the `drivers` collection (SWOOP-199), not by scoped user roles — requiring e.g.
  // `territory` here would 400 on unrelated user edits that merely happen to include the
  // DRIVER role. This value IS enforced, not a lookup-only default: `validateRoleScopes` in
  // apps/hetivo-back-admin-auth/src/user/user.service.ts treats `null` and `tenant`
  // identically as "no scope required" and skips the check for both.
  // SWOOP-199: a driver's real territory/location scope lives on their `scopes` array (synced
  // from the `drivers` record). Do NOT tighten this to `territory` — a location-attached driver
  // carries location-level scope only (no tenant/territory scope) and would then fail
  // create/update with a 400.
  DRIVER: ROLE_SCOPE.tenant,
  FLEET_MANAGER: ROLE_SCOPE.tenant,
  // SWOOP-378: an inspector works the sites they are given — either a whole territory or individual
  // locations — so ANY ONE of those two levels satisfies the role. A tenant-only scope does not: the
  // role is deliberately not tenant-wide. It is still module-bound, so only a tenant-wide caller (or Back
  // Office) may newly give it — a Territory Admin may not, since its grants exceed their own (G2, see
  // `UserAccessService.assertCanAssign`).
  INSPECTOR: [ROLE_SCOPE.territory, ROLE_SCOPE.location],
  LOCATION_MANAGER: ROLE_SCOPE.location,
  TENANT_ADMIN: ROLE_SCOPE.tenant,
  TERRITORY_ADMIN: ROLE_SCOPE.territory,
};

/** Narrows a role key from the wire (a plain string, possibly a custom role) to a built-in one. */
export function isBuiltInRoleKey(role: string): role is BuiltInRoleKey {
  // Own-key check (not `in`), so prototype keys such as `toString` are not mistaken for a role.
  return Object.prototype.hasOwnProperty.call(ROLE_REQUIRED_SCOPE, role);
}

/**
 * Every level a role accepts, as an array (empty when it needs no scope). The one place that turns
 * {@link RoleRequiredScope}'s three shapes into a list, so consumers never re-implement the unwrapping.
 */
export function getRequiredScopeLevels(role: BuiltInRoleKey): readonly RoleScope[] {
  const required = ROLE_REQUIRED_SCOPE[role];
  if (required === null) return [];
  return typeof required === 'string' ? [required] : required;
}

/**
 * SWOOP-378: the module a built-in role belongs to. A role listed here may only be NEWLY given to a user
 * of a tenant that owns the module (enforced by `UserService`; the front office hides it from the role
 * picker). A user who already holds it keeps it while the module is off — it just grants nothing until the
 * module is back on. Roles absent from the map are not module-bound.
 */
export const ROLE_REQUIRED_MODULE: Partial<Record<BuiltInRoleKey, ModuleAreaKey>> = {
  INSPECTOR: MODULE_AREA.Inspections,
};

/** The module `role` is bound to, or `undefined` for a custom role or a role with no module. */
export function getRoleRequiredModule(role: string): ModuleAreaKey | undefined {
  return isBuiltInRoleKey(role) ? ROLE_REQUIRED_MODULE[role] : undefined;
}

export interface Role extends BaseDocument {
  functions?: RoleFunctions;
  key: string;
  permissions: string[];
}
